import * as React from "react";
import { MessageSquareText } from "lucide-react";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";

import { useAddSupplierFamilyNoteMutation, useGetSupplierFamilyNotesQuery } from "@/api/suppliers";
import { useGetDirectoryUsersQuery } from "@/api/users-admin";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useDateFormatter } from "@/hooks/useDateFormatter";
import type { ContractFamilyNote, SupplierFamilySummary, SupplierStatus } from "@/types/supplier";

interface Props {
  supplierId: string;
  supplierStatus: SupplierStatus;
  family: SupplierFamilySummary;
}

interface FormData {
  body: string;
}

export const SupplierFamilyNotes = ({ supplierId, supplierStatus, family }: Props) => {
  const [offset, setOffset] = React.useState(0);
  const [loadedNotes, setLoadedNotes] = React.useState<ContractFamilyNote[]>([]);
  const [total, setTotal] = React.useState(0);
  const [nextOffset, setNextOffset] = React.useState<number | null>(null);
  const { currentData, isLoading, isFetching, isError, refetch } = useGetSupplierFamilyNotesQuery({
    supplierId,
    familyId: family.id,
    params: { offset, limit: 50 },
  });
  const [addNote, { isLoading: isAdding }] = useAddSupplierFamilyNoteMutation();
  const pageItems = currentData?.items;
  const pageOffset = currentData?.offset;
  const pageTotal = currentData?.total;
  const notes = React.useMemo(() => [...loadedNotes].reverse(), [loadedNotes]);
  const authorIds = React.useMemo(() => [...new Set(notes.map(note => note.author_user_id))], [notes]);
  const { data: authors = [] } = useGetDirectoryUsersQuery(authorIds, { skip: authorIds.length === 0 });
  const authorsById = React.useMemo(() => new Map(authors.map(author => [author.id, author])), [authors]);
  const { formatDateTime } = useDateFormatter();
  const {
    register,
    handleSubmit,
    reset,
    setError,
    formState: { errors },
  } = useForm<FormData>({ defaultValues: { body: "" } });

  React.useEffect(() => {
    setOffset(0);
    setLoadedNotes([]);
    setTotal(0);
    setNextOffset(null);
  }, [family.id]);

  React.useEffect(() => {
    if (!pageItems || pageOffset === undefined || pageTotal === undefined) return;
    setLoadedNotes(current => {
      if (pageOffset === 0) return pageItems;
      const existingIds = new Set(current.map(note => note.id));
      return [...current, ...pageItems.filter(note => !existingIds.has(note.id))];
    });
    setTotal(pageTotal);
    const followingOffset = pageOffset + pageItems.length;
    setNextOffset(pageItems.length > 0 && followingOffset < pageTotal ? followingOffset : null);
  }, [pageItems, pageOffset, pageTotal]);

  const onSubmit = async ({ body }: FormData) => {
    const trimmedBody = body.trim();
    if (!trimmedBody) {
      setError("body", { message: "Enter a note before adding it." });
      return;
    }
    try {
      await addNote({ supplierId, familyId: family.id, data: { body: trimmedBody } }).unwrap();
      setOffset(0);
      reset();
      toast.success("Note added");
    } catch {
      setError("root", { message: "The note could not be added. Refresh the page and try again." });
    }
  };

  const canAddNote = supplierStatus === "active" && family.can_edit;

  return (
    <section className="border-t border-[#ebe5de] px-5 py-5" aria-labelledby={`family-notes-${family.id}`}>
      <div className="mb-4 flex items-center gap-2">
        <MessageSquareText className="size-4 text-desert-sand-700" aria-hidden="true" />
        <h4 id={`family-notes-${family.id}`} className="text-sm font-semibold text-gray-950">
          Notes
        </h4>
        {total ? <span className="text-xs font-medium text-gray-500">{total}</span> : null}
      </div>

      {isLoading ? <p className="text-sm text-gray-500">Loading notes…</p> : null}
      {isError ? (
        <div className="flex items-center justify-between gap-3 rounded-md bg-red-50 px-3 py-2">
          <p role="alert" className="text-sm text-red-700">
            {loadedNotes.length > 0 ? "Older notes could not be loaded." : "Notes could not be loaded."}
          </p>
          <Button type="button" size="sm" variant="outline" onClick={() => void refetch()}>
            Retry loading notes
          </Button>
        </div>
      ) : null}
      {!isLoading && !isError && notes.length === 0 && (currentData?.items.length ?? 0) === 0 ? (
        <p className="rounded-md border border-dashed border-[#ddd4ca] bg-[#fbf8f4] px-4 py-5 text-sm text-gray-500">
          No notes have been added to this Contract Family.
        </p>
      ) : null}

      {notes.length > 0 ? (
        <>
          {!isError && nextOffset !== null ? (
            <div className="mb-3 flex justify-center">
              <Button
                type="button"
                size="sm"
                variant="ghost"
                disabled={isFetching}
                onClick={() => setOffset(nextOffset)}
              >
                {isFetching ? "Loading…" : "Load older notes"}
              </Button>
            </div>
          ) : null}
          <ol className="space-y-3">
            {notes.map(note => {
              const author = authorsById.get(note.author_user_id);
              const authorName = author ? `${author.name} ${author.surname}`.trim() || author.email : "Unknown user";
              return (
                <li key={note.id} data-family-note className="rounded-md border border-[#ebe5de] bg-white px-4 py-3">
                  <p className="whitespace-pre-wrap break-words text-sm leading-6 text-gray-800">{note.body}</p>
                  <p className="mt-2 text-xs font-medium text-gray-500">
                    {authorName} · <time dateTime={note.created_at}>{formatDateTime(note.created_at)}</time>
                  </p>
                </li>
              );
            })}
          </ol>
        </>
      ) : null}

      {canAddNote ? (
        <form className="mt-4 space-y-2" onSubmit={handleSubmit(onSubmit)}>
          <label htmlFor={`family-note-body-${family.id}`} className="text-sm font-semibold text-gray-800">
            Add note
          </label>
          <Textarea
            id={`family-note-body-${family.id}`}
            aria-label="Add a note about this Contract Family"
            maxLength={4000}
            placeholder="Add context for this Contract Family…"
            {...register("body", { maxLength: { value: 4000, message: "Notes can be up to 4,000 characters." } })}
          />
          {errors.body?.message ? (
            <p role="alert" className="text-sm text-red-700">
              {errors.body.message}
            </p>
          ) : null}
          {errors.root?.message ? (
            <p role="alert" className="text-sm text-red-700">
              {errors.root.message}
            </p>
          ) : null}
          <div className="flex justify-end">
            <Button type="submit" size="sm" disabled={isAdding}>
              {isAdding ? "Adding…" : "Add note"}
            </Button>
          </div>
        </form>
      ) : null}
    </section>
  );
};
